import { PPTCart } from '@/app/modules/ppt/services/ppt-cart.service';
import { PPTDelegated } from '@/app/modules/ppt/services/ppt-delegated.service';
import { selectPPTAccount } from '@/app/modules/ppt/state/entities/account/ppt-account.selectors';
import { selectPPTBill, selectPPTBillCartButtonTitle, selectPPTBillInCart, selectPPTBillIsByItselfPayable } from '@/app/modules/ppt/state/entities/bill/ppt-bill.selectors';
import { selectPPTParcelAddress } from '@/app/modules/ppt/state/entities/parcel/ppt-parcel.selectors';
import { selectPPTAutoDraftEnabled, selectPPTBlockMessages, selectPPTDisplayPrintBillButton, selectPPTStateLoaded, selectPPTSubscriptionsEnabled, selectPPTWatchListsEnabled } from '@/app/modules/ppt/state/ppt-state/ppt-state.selectors';
import { Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { AutoDraftService } from '@auto-draft/auto-draft.service';
import { sync } from '@ces/sync';
import { QuickModal } from '@egovsolutions/angular-quick-modal';
import { Store } from '@ngrx/store';
import { Viewport } from '@shared/services/viewport.service';
import { selectAppStateLoaded, selectUserAccountsEnabled } from '@/app/state/app-state/app-state.selectors';
import { State } from '@state/model';
import { SubscriptionsService } from '@subscriptions/subscriptions.service';
import { WatchListsDialogs } from '@watch-lists/services/watch-lists-dialogs.service';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
    selector: 'ppt-bill-actions',
    templateUrl: './ppt-bill-actions.component.html',
    styleUrls: ['./ppt-bill-actions.component.scss'],
})
export class BillActionsComponent
{
    constructor
    (
        // Dependencies

        private readonly store: Store<State>,
        private readonly pptCart: PPTCart,
        private readonly subscriptions: SubscriptionsService,
        private readonly autoDraft: AutoDraftService,
        private readonly watchListDialogs: WatchListsDialogs,
        private readonly quickModal: QuickModal,
        private readonly delegated: PPTDelegated,


        // Template dependencies

        protected viewport: Viewport,
    )
    {}


    // Customizations

    @Input() protected set billId (value: string | undefined) { this.billId$.next(value); }
    private readonly billId$ = new BehaviorSubject<string | undefined>(undefined);

    @Input() protected color = '#888888';


    // Services state and settings

    private readonly appStateLoaded$ = this.store.select(selectAppStateLoaded);
    protected readonly accountsEnabled$ = this.store.select(selectUserAccountsEnabled);
    protected readonly watchListsEnabled$ = this.store.select(selectPPTWatchListsEnabled);

    protected readonly servicesInfoLoaded$ = combineLatest(
        [this.appStateLoaded$, this.store.select(selectPPTStateLoaded)]).pipe(map(
            ([appStateLoaded, pptStateLoaded]) => appStateLoaded && pptStateLoaded
        ));

    protected readonly displayPrintBillButton$ = this.store.select(selectPPTDisplayPrintBillButton);


    // Block messages

    protected readonly blockMessages$ = this.store.select(selectPPTBlockMessages);


    // Bill info

    protected readonly bill$ = this.billId$.pipe(
        switchMap(billId => billId
            ? this.store.select(selectPPTBill(billId))
            : of(undefined)
        )
    );

    protected readonly isBillInCart$ = this.billId$.pipe(
        switchMap(billId => billId
            ? this.store.select(selectPPTBillInCart(billId))
            : of(undefined)
        ),
    );

    protected readonly accountNumber$ = this.bill$.pipe(map(bill => bill?.accountNumber));


    // Cart

    protected readonly addToOrRemoveFromCartTitle$ = this.billId$.pipe(
        switchMap(billId => billId ? this.store.select(selectPPTBillCartButtonTitle(billId)) : of(undefined))
    );

    protected readonly isItselfPayable$ = this.billId$.pipe(
        switchMap(id => id ? this.store.select(selectPPTBillIsByItselfPayable(id, true)) : of(undefined))
    );

    @ViewChild('paymentBlockedTooltip') private readonly paymentBlockedTooltip!: TemplateRef<any>;
    protected toggleBillInCartMenuItemClickHandler()
    {
        if (sync(this.bill$)?.paymentBlocked)
            this.quickModal.show(this.paymentBlockedTooltip, true);
        else if (sync(this.billOrDepsDelegated$))
            this.quickModal.show(sync(this.billOrDepsDelegatedMessage$)!, true);
        else
            this.toggleBillInCart();
    }

    protected readonly updatingCartState$ = new BehaviorSubject(false);

    protected toggleBillInCart()
    {
        if (this.billId$.value)
        {
            if (!sync(this.bill$)?.paymentBlocked && !this.updatingCartState$.value)
            {
                this.updatingCartState$.next(true);
                this.pptCart.toggleBillInCart(
                    this.billId$.value,
                    true,
                    true,
                    () => this.toggleBillInCart(),
                ).subscribe({
                    complete: () => this.updatingCartState$.next(false),
                    error: () => this.updatingCartState$.next(false),
                });
            }
        }
        else
            throw new Error('BillActionsComponent.toggleBillInCart() called without a bill available');
    }

    protected readonly billOrDepsDelegated$ = this.billId$.pipe(
        switchMap(billId => this.delegated.billOrDepsDelegated$(billId)),
    );

    private readonly billOrDepsDelegatedMessage$ = this.billId$.pipe(
        switchMap(billId => this.delegated.delegatedMessage$(billId)),
    );



    // Watch lists

    protected watchParcel()
    {
        const accountNumber = sync(this.accountNumber$);

        if (accountNumber)
        {
            const parcelId = sync(this.store.select(selectPPTAccount(accountNumber)))?.parcelId;

            if (parcelId)
            {
                const parcelAddress = sync(this.store.select(selectPPTParcelAddress(parcelId)));

                if (parcelAddress)
                    this.watchListDialogs.watchAccount(
                    {
                        type: 'ppt',
                        id: accountNumber,
                        humanId: parcelAddress,
                    });
            }
        }
        else
            throw new Error('BillActionsComponent.watchParcel() called without an accountNumber available');
    }


    // Subsctiptions

    protected readonly subscriptionsEnabled$ = this.store.select(selectPPTSubscriptionsEnabled);

    protected showSubscriptionsDialog()
    {
        const accountNumber = sync(this.accountNumber$);

        if (accountNumber)
            this.subscriptions.showDialog(accountNumber);
        else
            throw new Error('BillActionsComponent.showSubscriptionsDialog() called without an accountNumber available');
    }


    // Auto-draft

    protected readonly autoDraftEnabled$ = this.store.select(selectPPTAutoDraftEnabled);

    protected showAutoDraftDialog()
    {
        const accountNumber = sync(this.accountNumber$);

        if (accountNumber)
            this.autoDraft.showAutoDraftDialog(accountNumber);
        else
            throw new Error('BillActionsComponent.showAutoDraftDialog() called without an accountNumber available');
    }
}
